import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usersAPI } from '../services/api'
import PublicHeader from '../components/PublicHeader'
import { Plus, Search, Filter, AlertCircle, Building, MapPin, Users, Heart } from 'lucide-react'

// location komt uit de API als string óf als { city, country } — altijd naar tekst omzetten (anders React error #31)
const formatLocation = (loc) => {
  if (!loc) return ''
  if (typeof loc === 'string') return loc
  if (typeof loc === 'object') {
    return [loc.city, loc.region, loc.country].filter(v => typeof v === 'string' && v.trim()).join(', ')
  }
  return String(loc)
}

const CompaniesPage = () => {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    industry: '',
    location: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [totalCompanies, setTotalCompanies] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)

  const { user, isAuthenticated } = useAuth()
  const pageRef = useRef(1)

  useEffect(() => {
    setPage(1)
    pageRef.current = 1
    fetchCompanies(1)

    const intervalId = setInterval(() => {
      if (pageRef.current === 1) {
        fetchCompanies(1)
      }
    }, 30000)

    return () => clearInterval(intervalId)
  }, [filters])

  const fetchCompanies = async (requestedPage = 1) => {
    try {
      if (requestedPage === 1) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      const params = {
        search: searchTerm,
        ...filters,
        page: requestedPage,
        limit: 12
      }

      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key]
      })

      const response = await usersAPI.getCompanies(params)

      const transformedCompanies = response.data.companies.map(company => ({
        _id: company.id,
        name: company.name || '',
        logo: company.logo || '',
        bio: company.bio || '',
        industry: company.industry || '',
        specializations: company.specializations || [],
        location: formatLocation(company.location),
        website: company.website || '',
        companySize: company.companySize || '',
        socialMedia: company.socialMedia || {},
        joinedAt: company.joinedAt || '',
        role: company.role || 'bedrijf'
      }))

      if (requestedPage === 1) {
        setCompanies(transformedCompanies)
      } else {
        setCompanies(prev => [...prev, ...transformedCompanies])
      }

      const pagination = response.data.pagination
      if (pagination) {
        setHasNextPage(pagination.hasNextPage || false)
        setTotalCompanies(pagination.total || 0)
      }

      setPage(requestedPage)
      pageRef.current = requestedPage
    } catch (error) {
      console.error('Error fetching companies:', error)
      setError('Er is een fout opgetreden bij het ophalen van bedrijven')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const handleSearch = async (e) => {
    e.preventDefault()
    setPage(1)
    await fetchCompanies(1)
  }

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }))
  }

  const industries = [
    { value: '', label: 'Alle industrieën' },
    { value: 'E-commerce', label: 'E-commerce' },
    { value: 'Fashion & Mode', label: 'Fashion & Mode' },
    { value: 'Beauty & Cosmetica', label: 'Beauty & Cosmetica' },
    { value: 'Technologie', label: 'Technologie' },
    { value: 'Gezondheid & Fitness', label: 'Gezondheid & Fitness' },
    { value: 'Eten & Drinken', label: 'Eten & Drinken' },
    { value: 'Reizen', label: 'Reizen' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Vastgoed', label: 'Vastgoed' },
    { value: 'Entertainment', label: 'Entertainment' },
    { value: 'Sport', label: 'Sport' },
    { value: 'Overig', label: 'Overig' }
  ]

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })
  }

  return (
    <div className="font-['Inter'] min-h-screen bg-white text-[#0F172A] overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-[150%] h-[150%] bg-gradient-to-br from-blue-50/60 via-transparent to-transparent rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-[150%] h-[150%] bg-gradient-to-tl from-purple-50/60 via-transparent to-transparent rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {/* Header */}
      <PublicHeader />

      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-black mb-2">Ontdek Bedrijven</h1>
              <p className="text-[#475569] text-base md:text-lg">
                {totalCompanies || companies.length} bedrijven gevonden
              </p>
            </div>

            {isAuthenticated && (user?.role === 'bedrijf' || user?.role === 'agency') && (
              <div className="mt-4 sm:mt-0">
                <Link
                  to="/projects/create"
                  className="w-full sm:w-auto px-6 py-3 bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] transition inline-flex items-center justify-center text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Opdracht Plaatsen
                </Link>
              </div>
            )}
          </div>

          {/* Search & Filters */}
          <div className="mb-8">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
              <div className="flex-1 min-w-0 relative">
                <Search className="w-5 h-5 text-[#475569]/50 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Zoek op naam, industrie of specialisatie..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full min-w-0 pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] text-[#0F172A] text-base placeholder-[#475569]/50 shadow-md"
                />
              </div>
              <div className="grid grid-cols-2 sm:flex gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  aria-expanded={showFilters}
                  className="w-full sm:w-auto min-h-[48px] px-6 py-3 bg-white border border-gray-200 rounded-2xl hover:border-[#3EB489] transition-all inline-flex items-center justify-center font-medium shadow-md"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto min-h-[48px] px-6 py-3 bg-[#3EB489] rounded-2xl font-semibold hover:bg-[#359E78] transition text-white inline-flex items-center justify-center"
                >
                  Zoeken
                </button>
              </div>
            </form>

            {/* Filters Panel */}
            {showFilters && (
              <div className="relative mb-6">
                <div className="relative p-6 rounded-2xl bg-white border border-gray-200 shadow-md">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Industrie
                      </label>
                      <select
                        value={filters.industry}
                        onChange={(e) => handleFilterChange('industry', e.target.value)}
                        className="w-full min-w-0 min-h-[44px] bg-white border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] text-[#0F172A] text-base md:text-sm"
                      >
                        {industries.map(industry => (
                          <option key={industry.value} value={industry.value}>
                            {industry.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Locatie
                      </label>
                      <input
                        type="text"
                        placeholder="Stad of regio"
                        value={filters.location}
                        onChange={(e) => handleFilterChange('location', e.target.value)}
                        className="w-full min-w-0 min-h-[44px] bg-white border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] text-[#0F172A] text-base md:text-sm placeholder-[#475569]/50"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Companies Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-8 h-8 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-500">{error}</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
              <Building className="w-10 h-10 text-[#3EB489]" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Geen bedrijven gevonden</h3>
            <p className="text-[#475569] mb-8 max-w-md mx-auto">
              Probeer je zoekopdracht aan te passen of de filters te wijzigen.
            </p>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {companies.map((company) => (
              <div key={company._id} className="group relative min-w-0">
                <div className="relative p-6 rounded-2xl bg-white border border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300 transition-all">
                  {/* Header with Logo */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                      {company.logo ? (
                        <img
                          src={company.logo}
                          alt={company.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <img
                          src={`data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:rgb(59,130,246);stop-opacity:1" /><stop offset="100%" style="stop-color:rgb(147,51,234);stop-opacity:1" /></linearGradient></defs><circle cx="32" cy="32" r="32" fill="url(#grad)"/><text x="32" y="40" text-anchor="middle" fill="white" font-family="Arial" font-size="24" font-weight="bold">${(company.name || 'B').charAt(0).toUpperCase()}</text></svg>`)}`}
                          alt={company.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-[#0F172A] truncate">
                        {company.name}
                      </h3>
                      {company.location && (
                        <p className="text-sm text-[#475569] mb-2 flex items-center">
                          <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                          {company.location}
                        </p>
                      )}
                      {company.industry && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-[#1670c9] border border-blue-200">
                          {company.industry}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bio */}
                  <p className="text-[#475569] text-sm mb-4 line-clamp-2">
                    {company.bio}
                  </p>

                  {/* Specializations */}
                  {company.specializations?.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {company.specializations.slice(0, 3).map((spec, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"
                          >
                            {spec}
                          </span>
                        ))}
                        {company.specializations.length > 3 && (
                          <span className="text-xs text-[#475569]/50">
                            +{company.specializations.length - 3} meer
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Company Info */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-4 text-sm text-[#475569]">
                      {company.companySize && (
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {company.companySize}
                        </div>
                      )}
                      {company.role === 'agency' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Agency
                        </span>
                      )}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm text-[#475569]">
                      {company.joinedAt && (
                        <span>Lid sinds {formatDate(company.joinedAt)}</span>
                      )}
                    </div>

                    <Link
                      to={`/companies/${company._id}`}
                      className="inline-flex items-center justify-center min-h-[44px] px-6 py-2 bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] transition text-sm text-white"
                    >
                      Bekijk Profiel
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {hasNextPage && (
            <div className="flex justify-center mt-10">
              <button
                onClick={() => fetchCompanies(page + 1)}
                disabled={loadingMore}
                className="group relative"
              >
                <div className="relative px-8 py-3 bg-[#3EB489] rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center gap-2 text-white">
                  {loadingMore ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Laden...
                    </>
                  ) : (
                    'Meer bedrijven laden'
                  )}
                </div>
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Call to Action for non-authenticated users */}
      {!isAuthenticated && (
        <div className="relative mt-16 mb-8">
          <div className="relative max-w-4xl mx-auto text-center px-6 py-16 rounded-3xl bg-white border border-gray-200 shadow-md">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#3EB489] flex items-center justify-center">
              <Heart className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-black mb-4 text-[#3EB489]">
              Werk samen met top bedrijven
            </h2>
            <p className="text-lg text-[#475569] mb-8 max-w-2xl mx-auto">
              Meld je aan als creator om samen te werken met deze bedrijven of registreer je bedrijf op ons platform
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="group relative"
              >
                <div className="relative px-8 py-4 bg-[#3EB489] rounded-2xl font-semibold hover:bg-[#359E78] transition text-lg text-white">
                  Word Creator
                </div>
              </Link>
              <Link
                to="/register"
                className="group relative"
              >
                <div className="relative px-8 py-4 bg-white border-2 border-gray-200 rounded-2xl font-semibold hover:border-purple-400 hover:bg-purple-50 transition text-lg">
                  Registreer Bedrijf
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompaniesPage